/**
 * La colonna scura: due sezioni e le conversazioni.
 *
 * Netta e piena, attaccata al bordo. Sotto le due voci principali vivono le
 * conversazioni, come in ogni chat: sono la cronologia di quello che hai
 * chiesto, non una sezione a parte.
 *
 * Si può ridurre a una colonna di sole icone. La transizione è una sola —
 * la larghezza — e tutto il resto la segue: le etichette sfumano e scivolano
 * via mentre la colonna si stringe, le icone restano ferme al loro posto.
 * Niente che sparisca di colpo: è quello che fa sembrare «rotta» una UI.
 */

import { useState } from "react";
import {
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Trash2,
} from "lucide-react";
import type { ChatThreadView } from "@otto/shared";
import { Avatar, CountDot, cx } from "../ui/primitives.tsx";
import { SEZIONI, type NavItem, type View } from "./nav-items.tsx";

const CHIAVE = "otto.nav.compatta";

/** La durata della larghezza: un po' più lenta delle altre, perché sposta tutto. */
export const TRANSIZIONE_COLONNA =
  "transition-[width] duration-[420ms] ease-soft";

export function Nav({
  view,
  onGo,
  nomeUtente,
  badges,
  killSwitch,
  threads,
  threadId,
  onEliminaThread,
  onOpenThread,
}: {
  view: View;
  onGo: (view: View) => void;
  /** Il nome di chi è entrato: in fondo alla colonna c'è lui, non «Otto». */
  nomeUtente: string;
  badges: Partial<Record<View, number>>;
  killSwitch: boolean;
  threads: ChatThreadView[];
  threadId: string | null;
  onEliminaThread: (id: string) => void | Promise<void>;
  onOpenThread: (id: string | null) => void;
}) {
  // La conferma sta sulla riga: niente finestra di sistema per un elenco.
  const [daCancellare, setDaCancellare] = useState<string | null>(null);

  const [compatta, setCompatta] = useState(() => {
    try {
      return localStorage.getItem(CHIAVE) === "1";
    } catch {
      return false;
    }
  });
  const riduci = (valore: boolean) => {
    setCompatta(valore);
    try {
      localStorage.setItem(CHIAVE, valore ? "1" : "0");
    } catch {
      // Senza storage si perde solo la preferenza fra una sessione e l'altra.
    }
  };

  return (
    <nav
      className={cx(
        "flex h-full shrink-0 flex-col overflow-hidden bg-nav text-nav-ink",
        TRANSIZIONE_COLONNA,
        compatta ? "w-[76px]" : "w-[268px]",
      )}
    >
      {/* ── Marchio e bottone ────────────────────────────────────────── */}
      <div
        className={cx(
          "flex items-center pb-5 pt-6",
          compatta ? "flex-col gap-3 px-0" : "gap-2.5 px-5",
        )}
      >
        {/* Senza spazio fra logo ed etichetta quando è chiusa: altrimenti il
            logo resta 5 px a sinistra del centro, e si vede. */}
        <div
          className={cx(
            "flex items-center transition-[gap] duration-calm ease-soft",
            compatta ? "gap-0" : "gap-2.5",
          )}
        >
          <Logo />
          <Etichetta nascosta={compatta}>
            <span className="text-[19px] font-extrabold tracking-tight text-ink-inverse">
              Otto
            </span>
          </Etichetta>
        </div>
        <button
          type="button"
          aria-label={compatta ? "Allarga il menu" : "Riduci il menu"}
          title={compatta ? "Allarga il menu" : "Riduci il menu"}
          onClick={() => riduci(!compatta)}
          className={cx(
            "grid size-8 shrink-0 place-items-center rounded-field text-nav-ink transition-all duration-quick hover:bg-nav-soft hover:text-ink-inverse active:scale-90",
            !compatta && "ml-auto",
          )}
        >
          {compatta ? (
            <PanelLeftOpen size={17} />
          ) : (
            <PanelLeftClose size={17} />
          )}
        </button>
      </div>

      {/* ── Le due sezioni ───────────────────────────────────────────── */}
      <div className="flex flex-col gap-0.5 px-3">
        {SEZIONI.map((voce) => (
          <NavButton
            key={voce.id}
            voce={voce}
            active={view === voce.id}
            badge={badges[voce.id] ?? 0}
            alert={voce.id === "alerts" && killSwitch}
            compatta={compatta}
            onClick={() => onGo(voce.id)}
          />
        ))}
      </div>

      {/* ── Conversazioni ────────────────────────────────────────────── */}
      <div className="mt-6 flex min-h-0 flex-1 flex-col px-3">
        <div
          className={cx(
            "flex items-center pb-2",
            compatta ? "justify-center" : "justify-between px-3",
          )}
        >
          <Etichetta nascosta={compatta}>
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-nav-ink/45">
              Conversazioni
            </span>
          </Etichetta>
          <button
            type="button"
            aria-label="Nuova conversazione"
            title="Nuova conversazione"
            onClick={() => onOpenThread(null)}
            className={cx(
              "grid shrink-0 place-items-center rounded-pill text-nav-ink transition-all duration-quick hover:bg-nav-soft hover:text-ink-inverse active:scale-90",
              compatta ? "size-10 rounded-card bg-nav-soft/60" : "size-6",
            )}
          >
            <Plus size={compatta ? 18 : 15} />
          </button>
        </div>

        {/* L'elenco sfuma quando la colonna si stringe: un titolo troncato a
            tre lettere non dice niente, meglio niente. */}
        <div
          className={cx(
            "scroll-soft min-h-0 flex-1 overflow-y-auto transition-[opacity,transform] duration-calm ease-soft",
            compatta && "pointer-events-none -translate-x-2 opacity-0",
          )}
          aria-hidden={compatta}
        >
          {threads.length === 0 ? (
            <p className="whitespace-normal px-3 py-1 text-[12.5px] leading-relaxed text-nav-ink/45">
              Nessuna conversazione. Scrivi qui sotto quello che ti serve.
            </p>
          ) : (
            <div className="flex flex-col gap-0.5 pb-2">
              {threads.map((thread) => (
                <div
                  key={thread.id}
                  className={cx(
                    "group/riga flex items-center gap-1 rounded-card pr-1",
                    "transition-all duration-calm ease-soft",
                    thread.id === threadId
                      ? "bg-nav-soft"
                      : "hover:bg-nav-hover",
                  )}
                >
                  <button
                    type="button"
                    tabIndex={compatta ? -1 : 0}
                    onClick={() => onOpenThread(thread.id)}
                    className={cx(
                      "min-w-0 flex-1 truncate px-3 py-2 text-left text-[13px] font-medium",
                      thread.id === threadId
                        ? "text-ink-inverse"
                        : "text-nav-ink group-hover/riga:text-ink-inverse",
                    )}
                  >
                    {thread.title}
                  </button>
                  {daCancellare === thread.id ? (
                    <span className="flex shrink-0 items-center gap-0.5 text-[11.5px]">
                      <button
                        type="button"
                        tabIndex={compatta ? -1 : 0}
                        onClick={() => {
                          setDaCancellare(null);
                          void onEliminaThread(thread.id);
                        }}
                        className="rounded-field px-1.5 py-1 font-bold text-danger-soft hover:text-ink-inverse"
                      >
                        Cancella
                      </button>
                      <button
                        type="button"
                        tabIndex={compatta ? -1 : 0}
                        onClick={() => setDaCancellare(null)}
                        className="rounded-field px-1.5 py-1 text-nav-ink hover:text-ink-inverse"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    // Appare al passaggio del mouse: l'elenco resta pulito, e
                    // il comando che butta via qualcosa non sta sempre lì.
                    <button
                      type="button"
                      tabIndex={compatta ? -1 : 0}
                      aria-label={`Cancella «${thread.title}»`}
                      onClick={() => setDaCancellare(thread.id)}
                      className={cx(
                        "grid size-7 shrink-0 place-items-center rounded-field text-nav-ink",
                        "opacity-0 transition-opacity duration-quick",
                        "group-hover/riga:opacity-100 hover:text-ink-inverse",
                        "focus-visible:opacity-100",
                      )}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Chi è entrato ─────────────────────────────────────────────── */}
      {/* Il nome e basta (14/10/2026): «Assistente Otto» con il nome
          dell'account sotto diceva chi è Otto, che si sa già. Passandoci
          sopra la riga si accende e compare l'uscita. */}
      <div className="px-3 pb-3 pt-3">
        <div
          className={cx(
            "group/utente flex items-center gap-2.5 rounded-card-lg bg-nav-soft",
            "transition-all duration-calm ease-soft",
            "hover:-translate-y-0.5 hover:bg-surface hover:shadow-lift",
            "focus-within:bg-surface focus-within:shadow-lift",
            compatta ? "flex-col p-1.5" : "p-2 pr-1.5",
          )}
          title={compatta ? nomeUtente : undefined}
        >
          <span className="shrink-0 transition-transform duration-calm ease-soft group-hover/utente:scale-105">
            <Avatar name={nomeUtente} size={36} />
          </span>
          <Etichetta nascosta={compatta} className="min-w-0 flex-1">
            <p className="truncate text-[13.5px] font-bold text-ink-inverse transition-colors duration-calm group-hover/utente:text-ink group-focus-within/utente:text-ink">
              {nomeUtente}
            </p>
          </Etichetta>
          <button
            type="button"
            aria-label="Esci"
            title="Esci"
            onClick={() => {
              localStorage.removeItem("otto.token");
              window.location.reload();
            }}
            className={cx(
              "grid size-8 shrink-0 place-items-center rounded-field text-ink-faint",
              "opacity-0 transition-all duration-calm ease-soft",
              "-translate-x-1 group-hover/utente:translate-x-0 group-hover/utente:opacity-100",
              "focus-visible:translate-x-0 focus-visible:opacity-100",
              "hover:bg-surface-muted hover:text-ink",
            )}
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </nav>
  );
}

/**
 * Un'etichetta che sfuma e si ritira quando la colonna si stringe.
 *
 * Anima larghezza massima e opacità insieme: il testo non viene tagliato a
 * metà parola mentre scompare, si chiude come una tendina.
 */
function Etichetta({
  nascosta,
  className,
  children,
}: {
  nascosta: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      aria-hidden={nascosta}
      className={cx(
        "block overflow-hidden whitespace-nowrap transition-[max-width,max-height,opacity,transform] duration-calm ease-soft",
        nascosta
          ? "max-h-0 max-w-0 -translate-x-1 opacity-0"
          : "max-h-[80px] max-w-[200px] translate-x-0 opacity-100",
        className,
      )}
    >
      {children}
    </span>
  );
}

function NavButton({
  voce,
  active,
  badge,
  alert,
  compatta,
  onClick,
}: {
  voce: NavItem;
  active: boolean;
  badge: number;
  alert: boolean;
  compatta: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={compatta ? voce.label : undefined}
      aria-label={voce.label}
      className={cx(
        "group relative flex items-center rounded-card py-2.5 text-left text-[14px] font-semibold",
        "transition-all duration-calm ease-soft",
        compatta ? "justify-center px-0" : "gap-3 px-3",
        active
          ? "bg-nav-soft text-ink-inverse"
          : "text-nav-ink hover:bg-nav-hover hover:text-ink-inverse",
      )}
    >
      <voce.Icon
        size={18}
        strokeWidth={active ? 2.3 : 2}
        className="shrink-0"
      />
      <Etichetta nascosta={compatta} className="flex-1">
        <span className="flex items-center gap-2">
          <span className="flex-1 truncate">{voce.label}</span>
          <CountDot value={badge} />
          {alert ? (
            <span className="size-2 shrink-0 rounded-full bg-danger" />
          ) : null}
        </span>
      </Etichetta>
      {/* Con la colonna stretta il numero non ci sta: resta il pallino. */}
      {compatta && (badge > 0 || alert) ? (
        <span
          className={cx(
            "absolute right-2.5 top-2 size-2 rounded-full",
            alert ? "bg-danger" : "bg-accent",
          )}
        />
      ) : null}
    </button>
  );
}

/** Il segno: una casa che è anche una bolla di conversazione. */
function Logo() {
  return (
    <svg viewBox="0 0 32 32" className="size-8 shrink-0" aria-hidden>
      <path
        d="M16 3.5 4.5 12.2v13.4c0 1.6 1.3 2.9 2.9 2.9h17.2c1.6 0 2.9-1.3 2.9-2.9V12.2L16 3.5Z"
        fill="var(--color-accent)"
      />
      <path
        d="M12.2 17.4h7.6M12.2 21.2h4.8"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
